import { useState, useEffect } from 'react'
import { ApiService } from '../../../api/services.gen'
import { useAlert } from '../../../constants/AlertProvider'
import { XMarkIcon } from "@heroicons/react/24/solid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// A stored date is a plain "YYYY-MM-DD". `new Date("2021-01-01")` parses that
// as UTC midnight, which renders as the previous year in negative-offset
// timezones (showing 2020 for a 2021 date). Build the date in local time so the
// year displayed always matches the year stored.
const parseStoredYear = (value) => {
    if (!value) return null;
    const match = /^(\d{4})/.exec(value);
    return match ? new Date(Number(match[1]), 0, 1) : null;
};

export function AddEducation({ applicationId }) {
    const [educationList, setEducationList] = useState([]);
    const [institution, setInstitution] = useState("");
    const [degree, setDegree] = useState("");
    const [major, setMajor] = useState("");
    const [gpa, setGpa] = useState("");
    const [startYear, setStartYear] = useState(null);
    const [endYear, setEndYear] = useState(null);
    const showAlert = useAlert();

    useEffect(() => {
        const fetchEducation = async () => {
            if (!applicationId) return
            try {
                const res = await ApiService.apiEducationList({ application: applicationId })
                setEducationList(res.results || res)
            } catch (e) {
                showAlert("Failed to load education", "error");
            }
        }
        fetchEducation();
    }, [applicationId, showAlert])

    const addEducation = async () => {
        if (educationList.length >= 2) {
            showAlert("Only 2 education can be added", "error");
            return;
        }
        const newEdu = {
            institution,
            degree,
            field_of_study: major,
            gpa,
            start_date: startYear ? `${startYear.getFullYear()}-01-01` : null,
            end_date: endYear ? `${endYear.getFullYear()}-01-01` : null,
            application: applicationId,
        }
        try {
            const res = await ApiService.apiEducationCreate({ requestBody: newEdu })
            setEducationList([...educationList, res])
            // Reset form
            setInstitution(""); setDegree(""); setMajor(""); setGpa(""); setStartYear(null); setEndYear(null);
        } catch (error) {
            showAlert("Failed to add education", "error");
            console.log("Failed to add education", error);
        }
    }

    const handleFieldChange = (id, field, value) => {
        setEducationList(
            educationList.map((edu) =>
                edu.id === id ? { ...edu, [field]: value } : edu
            )
        );
    };

    const updateEducation = async (id) => {
        const updatedEdu = educationList.find((edu) => edu.id === id);
        if (!updatedEdu) return;

        try {
            await ApiService.apiEducationPartialUpdate({
                id,
                requestBody: updatedEdu,
            });
            showAlert("Education updated successfully!", "success");
        } catch (e) {
            showAlert("Failed to update education", "error");
        }
    };

    const removeEducation = async (id) => {
        try {
            await ApiService.apiEducationDestroy({ id })
            setEducationList(educationList.filter((edu) => edu.id !== id))
        } catch (e) {
            showAlert("Failed to delete education", "error");
        }
    }

    return (
        <div className="flex flex-col gap-y-4">
            {educationList.map((edu) => (
                <div key={edu.id} className="p-2 flex flex-col gap-2 mb-2 bg-gray-50 dark:bg-gray-800">
                    <div className="flex justify-between items-center">
                        <span className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">{edu.degree} in {edu.field_of_study} from {edu.institution}</span>
                        <button
                            className="text-gray-400 dark:text-gray-500 flex flex-row gap-x-2 items-center justify-center hover:text-gray-500 dark:hover:text-gray-300 cursor-pointer"
                            onClick={() => removeEducation(edu.id)}
                        >
                            <XMarkIcon className="size-4" />
                            <span className="text-[14px] font-medium">Remove</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">School</label>
                            <input
                                type="text"
                                value={edu.institution || ""}
                                onChange={e => handleFieldChange(edu.id, "institution", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Degree</label>
                            <input
                                type="text"
                                value={edu.degree || ""}
                                onChange={e => handleFieldChange(edu.id, "degree", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Major</label>
                            <input
                                type="text"
                                value={edu.field_of_study || ""}
                                onChange={e => handleFieldChange(edu.id, "field_of_study", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">GPA</label>
                            <input
                                type="number"
                                value={edu.gpa || ""}
                                onChange={e => handleFieldChange(edu.id, "gpa", e.target.value)}
                                className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Start year</label>
                            <DatePicker
                                selected={parseStoredYear(edu.start_date)}
                                onChange={date => handleFieldChange(edu.id, "start_date", date ? `${date.getFullYear()}-01-01` : null)}
                                showYearPicker
                                dateFormat="yyyy"
                                minDate={new Date(1990, 0, 1)}
                                maxDate={new Date(2030, 11, 31)}
                                className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-2"
                            />
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">End year</label>
                            <DatePicker
                                selected={parseStoredYear(edu.end_date)}
                                onChange={date => handleFieldChange(edu.id, "end_date", date ? `${date.getFullYear()}-01-01` : null)}
                                showYearPicker
                                dateFormat="yyyy"
                                minDate={new Date(1990, 0, 1)}
                                maxDate={new Date(2030, 11, 31)}
                                className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-2"
                            />
                        </div>
                    </div>
                    <div className='flex justify-end'>
                        <button
                            type="button"
                            className="text-[14px] font-semibold text-blue-700 dark:text-blue-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-300 flex items-center gap-1"
                            onClick={() => updateEducation(edu.id)}
                        >
                            Update
                        </button>
                    </div>
                </div>
            ))}

            {educationList.length < 2 ? (
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">School</label>
                        <input
                            type="text"
                            value={institution}
                            onChange={e => setInstitution(e.target.value)}
                            className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                        />
                    </div>

                    <div className="flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Degree</label>
                        <input
                            type="text"
                            value={degree}
                            onChange={e => setDegree(e.target.value)}
                            className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                        />
                    </div>

                    <div className="flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Start year</label>
                        <DatePicker
                            selected={startYear}
                            onChange={date => setStartYear(date)}
                            showYearPicker
                            dateFormat="yyyy"
                            minDate={new Date(1990, 0, 1)}
                            maxDate={new Date(2030, 11, 31)}
                            className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                        />
                    </div>

                    <div className="flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">End year</label>
                        <DatePicker
                            selected={endYear}
                            onChange={date => setEndYear(date)}
                            showYearPicker
                            dateFormat="yyyy"
                            minDate={new Date(1990, 0, 1)}
                            maxDate={new Date(2030, 11, 31)}
                            className="w-full text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                        />
                    </div>

                    <div className="flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">Major</label>
                        <input
                            type="text"
                            value={major}
                            onChange={e => setMajor(e.target.value)}
                            className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                        />
                    </div>

                    <div className="flex flex-col gap-y-2">
                        <label className="font-medium text-gray-900 dark:text-gray-100 text-[14px]">GPA</label>
                        <input
                            type="number"
                            value={gpa}
                            onChange={e => setGpa(e.target.value)}
                            className="text-[14px] text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 rounded-lg p-2"
                        />
                    </div>
                </div>
            ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">You can only add up to 2 education entries.</div>
            )}

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={addEducation}
                    className={`text-[14px] font-semibold ${educationList.length >= 2 ? "cursor-none text-blue-300 dark:text-blue-800" : "text-blue-700 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer"} flex items-center gap-1`}
                    disabled={educationList.length >= 2}
                >
                    Add Education
                </button>
            </div>

        </div>
    );
}